'use client'

import { Children, Fragment, isValidElement, useEffect, useRef, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

// ============================================================================
// СПИСОК ВАРИАНТОВ — общий для Select и Combobox.
// ----------------------------------------------------------------------------
// Один вид пункта, группы, подсказки справа (код страны, валюты) и отметки
// выбранного. Навигация стрелками живёт у владельца (он же держит поле ввода),
// список только рисует и прокручивает подсвеченный пункт в видимую часть.
// ============================================================================

export interface SelectOption<V extends string = string> {
  value: V
  /** Подпись в списке и в поле. */
  label: ReactNode
  /** Текст для поиска, если label не строка. */
  text?: string
  /** Приглушённая подпись справа: код, единица, количество. */
  hint?: ReactNode
  /** Заголовок группы; пункты одной группы должны идти подряд. */
  group?: string
  disabled?: boolean
}

/** Текст пункта для поиска и для поля: label-строка или явный text. */
export function optionText(o: SelectOption<string>): string {
  if (o.text != null) return o.text
  return nodeText(o.label)
}

function nodeText(n: ReactNode): string {
  if (n == null || typeof n === 'boolean') return ''
  if (typeof n === 'string' || typeof n === 'number') return String(n)
  if (Array.isArray(n)) return n.map(nodeText).join('')
  if (isValidElement(n)) return nodeText((n.props as { children?: ReactNode }).children)
  return ''
}

/** Фильтр по подстроке, без учёта регистра; ищет и в подписи, и в значении. */
export function filterOptions<V extends string>(options: SelectOption<V>[], query: string): SelectOption<V>[] {
  const q = query.trim().toLowerCase()
  if (!q) return options
  return options.filter(o => optionText(o).toLowerCase().includes(q) || o.value.toLowerCase().includes(q))
}

/**
 * Варианты из детей `<option>` / `<optgroup>` — мост для старой разметки
 * нативного `<select>`: `<Select value onChange><option …/></Select>` работает
 * без переписывания вызова. Фрагменты и массивы разворачиваются, `false`/`null`
 * пропускаются.
 */
export function optionsFromChildren(children: ReactNode, group?: string): SelectOption<string>[] {
  const out: SelectOption<string>[] = []
  Children.forEach(children, child => {
    if (!isValidElement(child)) return
    const props = child.props as Record<string, any>
    if (child.type === Fragment) {
      out.push(...optionsFromChildren(props.children, group))
    } else if (child.type === 'optgroup') {
      out.push(...optionsFromChildren(props.children, String(props.label ?? '')))
    } else if (child.type === 'option') {
      const label = props.children ?? props.label ?? props.value
      out.push({
        value: String(props.value ?? nodeText(label)),
        label,
        text: nodeText(label),
        group,
        disabled: !!props.disabled,
      })
    } else if (props.value != null || props.code != null) {
      // Компонент-обёртка над <option> (CurrencyOption): берём его пропсы.
      const value = String(props.value ?? props.code)
      const label = props.label ?? props.children ?? value
      out.push({ value, label, text: nodeText(label), group, disabled: !!props.disabled })
    }
  })
  return out
}

export function OptionList<V extends string>({
  id, options, selected, active, onPick, onHover, empty,
}: {
  id: string
  options: SelectOption<V>[]
  /** Выбранные значения — отметка галочкой. */
  selected: ReadonlySet<string>
  /** Индекс подсвеченного (клавиатурой или мышью). */
  active: number
  onPick: (o: SelectOption<V>) => void
  onHover: (i: number) => void
  empty: ReactNode
}) {
  const listRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [active])

  if (options.length === 0) {
    return <div className="px-3 py-3 text-[13px] text-ink-500">{empty}</div>
  }

  return (
    <div ref={listRef} role="listbox" id={id} className="overflow-y-auto py-1" style={{ maxHeight: 'var(--panel-max-h, 320px)' }}>
      {options.map((o, i) => {
        const header = o.group && (i === 0 || options[i - 1].group !== o.group) ? o.group : null
        const isSelected = selected.has(o.value)
        return (
          <Fragment key={`${o.group ?? ''}:${o.value}`}>
            {header && (
              <div className="px-3 pb-1 pt-2.5 text-[11px] font-semibold uppercase tracking-[0.06em] text-ink-400">
                {header}
              </div>
            )}
            <div
              role="option"
              id={`${id}-${i}`}
              data-index={i}
              aria-selected={isSelected}
              aria-disabled={o.disabled || undefined}
              // mousedown, а не click: поле не теряет фокус, и выбор не
              // спорит с blur-закрытием.
              onMouseDown={e => { e.preventDefault(); if (!o.disabled) onPick(o) }}
              onMouseMove={() => { if (i !== active) onHover(i) }}
              className={cn(
                'mx-1 flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-[7px] text-[13px]',
                o.disabled ? 'cursor-not-allowed text-ink-300' : 'text-ink-800',
                i === active && !o.disabled && 'bg-ink-50',
                isSelected && 'font-semibold text-accent-700',
              )}
            >
              <span className="min-w-0 flex-1 truncate" suppressHydrationWarning>{o.label}</span>
              {o.hint != null && <span className="shrink-0 text-[12px] font-normal text-ink-400">{o.hint}</span>}
              <Check
                size={14}
                strokeWidth={2}
                aria-hidden
                className={cn('shrink-0 text-accent-600', isSelected ? 'opacity-100' : 'opacity-0')}
              />
            </div>
          </Fragment>
        )
      })}
    </div>
  )
}

/** Следующий доступный индекс в направлении dir (по кругу). */
export function stepIndex(options: SelectOption<string>[], from: number, dir: 1 | -1): number {
  const n = options.length
  if (n === 0) return -1
  for (let k = 1; k <= n; k++) {
    const i = (from + dir * k + n * 2) % n
    if (!options[i].disabled) return i
  }
  return -1
}
